import React, { useMemo, useState } from 'react'
import { useParamountAuth } from 'context/ParamountAuthContext'
import {
  getClients,
  getProjectSites,
  getCompanyEmployees,
  getActivityLogs,
  fetchSiteChecklistPdf,
  fetchHazardReportPdf,
} from '../../../services/clients.svc'
import { mediumTime } from 'common/constants'
import './ActivityLogsPage.scss'

// Maps event types that have a downloadable PDF to the fetcher + filename prefix
const FILE_FETCHERS = {
  checklist_submitted: { fetchPdf: fetchSiteChecklistPdf, filePrefix: 'site-inspection-checklist' },
  hazard_submitted: { fetchPdf: fetchHazardReportPdf, filePrefix: 'hazard-report' },
  incident_submitted: { fetchPdf: fetchHazardReportPdf, filePrefix: 'incident-report' },
}

async function downloadActivityLogFile(companyId, log) {
  const fetcher = FILE_FETCHERS[log.type]
  if (!fetcher || !log.recordId) return
  try {
    const res = await fetcher.fetchPdf(companyId, log.recordId)
    if (!res.ok) throw new Error('PDF not available')
    const blob = await res.blob()
    const objectUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = `${fetcher.filePrefix}-${log.recordId}.pdf`
    a.click()
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000)
  } catch (e) {
    console.error('Activity log file download failed', e)
    alert('File is not available for download. Please try again shortly.')
  }
}

const EVENT_LABELS = {
  checkin: 'Check-in',
  checkout: 'Check-out',
  swms_signed: 'SWMS Signed',
  hazard_submitted: 'Hazard Report',
  incident_submitted: 'Incident Report',
  checklist_submitted: 'Checklist Submitted',
}

const EVENT_TYPES = Object.keys(EVENT_LABELS)

// SuperUser-only audit tool. Aggregates checkin/checkout, SWMS signatures,
// hazard/incident report submissions and site checklist submissions for a
// company into one unified, filterable timeline. Accessible only via direct
// URL (/bananastuff/activityLogs) — no nav link.
export default function ActivityLogsPage() {
  const { userRoles } = useParamountAuth()
  const isSuperUser = (userRoles || []).includes('SuperUser')

  const [companies, setCompanies] = useState([])
  const [companiesLoading, setCompaniesLoading] = useState(false)
  const [companiesLoaded, setCompaniesLoaded] = useState(false)
  const [selectedCompanyId, setSelectedCompanyId] = useState('')

  const [sites, setSites] = useState([])
  const [employees, setEmployees] = useState([])
  const [selectedSiteId, setSelectedSiteId] = useState('')
  const [selectedUserRoleId, setSelectedUserRoleId] = useState('')
  const [selectedEventType, setSelectedEventType] = useState('')

  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const [logs, setLogs] = useState([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [error, setError] = useState('')

  const loadCompanies = async () => {
    if (companiesLoaded || companiesLoading) return
    setCompaniesLoading(true)
    setError('')
    try {
      const result = await getClients()
      setCompanies(result || [])
      setCompaniesLoaded(true)
    } catch (err) {
      setError(err?.message || 'Failed to load clients.')
    } finally {
      setCompaniesLoading(false)
    }
  }

  const fetchLogs = async (companyId, overrides = {}) => {
    setLogsLoading(true)
    setError('')
    try {
      const params = {
        siteId: overrides.siteId ?? selectedSiteId,
        userRoleId: overrides.userRoleId ?? selectedUserRoleId,
        dateFrom: overrides.dateFrom ?? dateFrom,
        dateTo: overrides.dateTo ?? dateTo,
      }
      const result = await getActivityLogs(companyId, params)
      setLogs(result || [])
    } catch (err) {
      setError(err?.message || 'Failed to load activity logs.')
    } finally {
      setLogsLoading(false)
    }
  }

  const handleCompanyChange = async (companyId) => {
    setSelectedCompanyId(companyId)
    setSelectedSiteId('')
    setSelectedUserRoleId('')
    setLogs([])
    setError('')
    if (!companyId) {
      setSites([])
      setEmployees([])
      return
    }
    try {
      const [siteResult, employeeResult] = await Promise.all([
        getProjectSites(companyId),
        getCompanyEmployees(companyId),
      ])
      setSites(siteResult || [])
      setEmployees(employeeResult || [])
    } catch (err) {
      setError(err?.message || 'Failed to load sites/employees for this company.')
    }
    await fetchLogs(companyId, { siteId: '', userRoleId: '' })
  }

  const handleFilterChange = (patch) => {
    if ('siteId' in patch) setSelectedSiteId(patch.siteId)
    if ('userRoleId' in patch) setSelectedUserRoleId(patch.userRoleId)
    if ('dateFrom' in patch) setDateFrom(patch.dateFrom)
    if ('dateTo' in patch) setDateTo(patch.dateTo)
    if (selectedCompanyId) fetchLogs(selectedCompanyId, patch)
  }

  const filteredLogs = useMemo(() => {
    if (!selectedEventType) return logs
    return logs.filter((l) => l.type === selectedEventType)
  }, [logs, selectedEventType])

  if (!isSuperUser) {
    return <div className="activity-logs-page">Unauthorized</div>
  }

  return (
    <div className="activity-logs-page">
      <div className="activity-logs-title">Activity Logs</div>
      <div className="activity-logs-subtitle">
        Audit trail of supervisor/tradie activity — check-ins/outs, SWMS signatures, hazard/incident
        reports, and checklist submissions. SuperUser only.
      </div>

      <div className="activity-logs-filters">
        <div className="filter-group">
          <label>Company</label>
          <select
            value={selectedCompanyId}
            onFocus={loadCompanies}
            onChange={(e) => handleCompanyChange(e.target.value)}
          >
            <option value="">{companiesLoading ? 'Loading…' : 'Select a company…'}</option>
            {companies.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Site / Job No.</label>
          <select
            value={selectedSiteId}
            onChange={(e) => handleFilterChange({ siteId: e.target.value })}
            disabled={!selectedCompanyId}
          >
            <option value="">All sites</option>
            {sites.map((s) => (
              <option key={s._id} value={s._id}>
                {s.siteNumber ? `${s.siteNumber} — ${s.address}` : s.address}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>User</label>
          <select
            value={selectedUserRoleId}
            onChange={(e) => handleFilterChange({ userRoleId: e.target.value })}
            disabled={!selectedCompanyId}
          >
            <option value="">All users</option>
            {employees.map((e) => (
              <option key={e.userRole?._id || e._id} value={e.userRole?._id || e._id}>
                {e.fullName} {e.role ? `(${e.role})` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Event Type</label>
          <select value={selectedEventType} onChange={(e) => setSelectedEventType(e.target.value)}>
            <option value="">All events</option>
            {EVENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {EVENT_LABELS[t]}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => handleFilterChange({ dateFrom: e.target.value })}
          />
        </div>

        <div className="filter-group">
          <label>To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => handleFilterChange({ dateTo: e.target.value })}
          />
        </div>
      </div>

      {error && <div className="activity-logs-error">{error}</div>}

      {selectedCompanyId && (
        <div className="activity-logs-summary">
          {logsLoading ? 'Loading logs…' : `${filteredLogs.length} event(s) shown`}
        </div>
      )}

      {selectedCompanyId && !logsLoading && (
        <div className="activity-logs-table-wrapper">
          <table className="activity-logs-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Event</th>
                <th>User</th>
                <th>Job No.</th>
                <th>Site Address</th>
                <th>Detail</th>
                <th>File</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((l, i) => (
                <tr key={i}>
                  <td>{l.timestamp ? mediumTime.format(new Date(l.timestamp)) : '—'}</td>
                  <td>
                    <span className={`event-badge event-badge-${l.type}`}>
                      {EVENT_LABELS[l.type] || l.type}
                    </span>
                  </td>
                  <td>{l.userName || '—'}</td>
                  <td>{l.siteNumber || '—'}</td>
                  <td>{l.siteAddress || '—'}</td>
                  <td>{l.detail || '—'}</td>
                  <td>
                    {FILE_FETCHERS[l.type] && l.hasFile ? (
                      <button
                        type="button"
                        className="activity-logs-download-btn"
                        onClick={() => downloadActivityLogFile(selectedCompanyId, l)}
                      >
                        Download
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
              {!filteredLogs.length && (
                <tr>
                  <td colSpan={7} className="activity-logs-empty">
                    No activity matches the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
